import { MessageTypeColor } from "@/types";
import {
  botMessageColors,
  botMessageColorsDark,
  userMessageColors,
  userMessageColorsDark,
} from "../../../../colors/colors";
import { useTheme } from "@mui/material/styles";

export const useTextMessageStyles = (
  isBot: boolean,
  variant: MessageTypeColor = "default",
) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const messageColors =
    isBot || variant === "system"
      ? isDark
        ? botMessageColorsDark
        : botMessageColors
      : isDark
        ? userMessageColorsDark
        : userMessageColors;

  const colors = messageColors[variant] || messageColors.default;

  return {
    container: {
      background: colors.background,
      borderRadius: 3,
      p: 2,
      color: colors.text,
      fontFamily: "Red Hat Display",
      fontWeight: 500,
      ...(variant === "system" && {
        background:
          "linear-gradient(90deg, #28C9DC 0%, #8957CF 100%) !important",
        color: "#ffffff !important",
      }),
    },
    messageRow: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "baseline",
      color: colors.text,
      fontFamily: "Red Hat Display",
      fontWeight: 500,
      ...(variant === "system" && {
        color: "#ffffff !important",
      }),
    },
    titleRow: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "baseline",
      color: colors.text,
      fontFamily: "Red Hat Display",
      fontWeight: 500,
      ...(variant === "system" && {
        color: "#ffffff !important",
      }),
    },
    timestamp: {
      fontSize: "12px",
      whiteSpace: "nowrap",
      alignSelf: "flex-end",
      color: colors.timeColor,
      fontFamily: "Red Hat Display",
      fontWeight: 500,
    },
    collapseContainer: {
      display: "inline-flex",
      flexWrap: "wrap",
      alignItems: "baseline",
      backgroundColor: colors.background,
      borderRadius: 3,
      p: 2,
      color: colors.text,
      fontFamily: "Red Hat Display",
      fontWeight: 500,
      ...(variant === "system" && {
        background:
          "linear-gradient(90deg, #28C9DC 0%, #8957CF 100%) !important",
        color: "#ffffff !important",
      }),
    },
    collapseToggle: {
      display: "flex",
      cursor: "pointer",
    },
    collapseToggleText: {
      mr: 0.5,
      color: "#6378f0",
      fontFamily: "Red Hat Display",
      fontWeight: 500,
    },
    collapseToggleIcon: (isCollapsed: boolean) => ({
      fontSize: 18,
      transform: isCollapsed ? "rotate(0deg)" : "rotate(180deg)",
      transition: "transform 0.3s",
      color: "#6378f0",
      fontFamily: "Red Hat Display",
      fontWeight: 500,
    }),
    collapseTimestamp: {
      opacity: 0.7,
      fontSize: "0.75rem",
      whiteSpace: "nowrap",
      alignSelf: "flex-end",
      fontFamily: "Red Hat Display",
      fontWeight: 500,
    },
    collapsedReplySnippet: {
      maxHeight: "4.5em",
      overflow: "hidden",
      marginBottom: "10px",
      paddingBottom: "6px",
      lineHeight: 1.45,
      boxSizing: "border-box",
      WebkitOverflowScrolling: "touch",
    },
    feedbackContainer: (embedded: boolean) => ({
      background: embedded ? "inherit" : theme.palette.flashColors.flashBg,
      borderRadius: "8px 8px 18px 18px",
      mb: embedded ? 0 : 2,
      position: "relative",
      color: colors.text,
      fontFamily: "Red Hat Display",
      fontWeight: 500,
    }),
    feedbackHeader: {
      background: theme.palette.flashColors.flashGradient,
      textAlign: "center",
      borderRadius: "16px 16px 0 0",
      p: "10px 14px",
      mb: 1.5,
      color: "#e7eefc",
      fontSize: 20,
      fontWeight: "bold",
      fontFamily: "Red Hat Display",
    },
    feedbackContent: (
      isLongText: boolean,
      isExpanded: boolean,
      expandedHeight: number,
      collapsedHeight: number,
    ) => ({
      p: "12px 22px",
      fontFamily: "Red Hat Display",
      width: "100%",
      maxWidth: "100%",
      overflow: "hidden",
      maxHeight: !isLongText
        ? "none"
        : isExpanded
          ? `${expandedHeight}px`
          : `${collapsedHeight}px`,
      opacity: isExpanded || !isLongText ? 1 : 0.97,
      transition:
        "max-height 360ms cubic-bezier(0.22, 1, 0.36, 1), opacity 260ms ease, transform 260ms ease",
      transform:
        isExpanded || !isLongText ? "translateY(0)" : "translateY(-1px)",
      position: "relative",
      ...(!isLongText || isExpanded
        ? {}
        : {
            "&::after": {
              content: '""',
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: isDark ? "72px" : "42px",
              pointerEvents: "none",
              background: isDark
                ? "linear-gradient(180deg, rgba(2, 8, 23, 0) 0%, rgba(2, 8, 23, 0.72) 45%, rgba(2, 8, 23, 0.98) 100%)"
                : "linear-gradient(180deg, rgba(238, 236, 252, 0) 0%, rgba(238, 236, 252, 0.98) 100%)",
              ...(isDark && {
                backdropFilter: "blur(2px)",
                WebkitBackdropFilter: "blur(2px)",
              }),
            },
          }),
    }),
    feedbackContentInner: {
      display: "flow-root",
      pb: "6px",
      "& .markdown": {
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
      },
      "& .markdown table": {
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        tableLayout: "fixed",
      },
      "& .markdown th, & .markdown td": {
        wordBreak: "normal",
        overflowWrap: "break-word",
        fontSize: { xs: "10px", sm: "11px", md: "16px" },
        lineHeight: { xs: 1.2, sm: 1.25, md: 1.35 },
        padding: { xs: "4px 6px", sm: "6px 8px", md: "8px 10px" },
      },
    },
    feedbackFooter: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      mt: 1,
      p: "14px 20px 16px",
    },
    feedbackToggle: {
      display: "flex",
      alignItems: "center",
      cursor: "pointer",
    },
    feedbackToggleText: {
      color: colors.linkColor || "#06B6D4",
      fontFamily: "Red Hat Display",
      fontWeight: 500,
    },
    feedbackToggleIcon: (isExpanded: boolean) => ({
      fontSize: 18,
      transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
      transition: "transform 0.3s",
      color: colors.linkColor || "#06B6D4",
      fontFamily: "Red Hat Display",
      fontWeight: 500,
    }),
  };
};
